import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { chain, difference, map } from 'lodash';
import { subscriptionAPI } from 'redux/subscriptionAPI';
import { availablePageSizes } from 'config';
import { createPaginationAdapter } from '@payer-admin-ui/components-v2/src/state/paginationAdapter';
import { createFilterAdapter } from '@payer-admin-ui/components-v2/src/state/filterAdapter';
import { patch } from '@payer-admin-ui/components-v2/src/functions/lodashExtensions';

export const fetchSubscription = createAsyncThunk('subscriptions/fetchById', async ({ subscriptionId }) => {
  return subscriptionAPI.fetchById({ subscriptionId });
});

export const fetchSubscriptions = createAsyncThunk('subscriptions/fetchPage', async (_, { getState }) => {
  const { page, pageSize, appliedFilters } = getState().subscriptions.global.listView;
  return subscriptionAPI.fetchPage({ page, pageSize, filters: appliedFilters });
});

export const cancelSubscription = createAsyncThunk('subscriptions/setEnabled', async ({ subscriptionId }) => {
  return subscriptionAPI.cancelSubscription({ subscriptionId });
});

const SINGLE_CACHE_SIZE = 10;

const emptyFilters = {
  statuses: [],
  deliveryTypes: [],
  subscriptionPaymentMethods: [],
  endDateStart: '',
  endDateEnd: '',
  nextBillingDateStart: '',
  nextBillingDateEnd: '',
  search: '',
};

const entityAdapter = createEntityAdapter();
const paginationAdapter = createPaginationAdapter({
  selectState: state => state.listView,
});
const filterAdapter = createFilterAdapter({
  selectState: state => state.listView,
  emptyFilters,
  reset: paginationAdapter.reset,
});

const initialFilters = {
  ...emptyFilters,
};

const initialState = entityAdapter.getInitialState({
  singleView: {
    cachedIds: [],
  },
  listView: {
    isFetching: false,
    error: null,
    totalCount: 0,
    ids: [],
    page: 0, // zero-indexed in state, one-indexed in UI
    pageSize: availablePageSizes[0],
    filters: initialFilters,
    appliedFilters: initialFilters,
  },
});

entityAdapter.pruneCache = state => {
  entityAdapter.removeMany(state, difference(state.ids, state.singleView.cachedIds, state.listView.ids));
};

entityAdapter.upsertOnePruneCache = (state, entity) => {
  entityAdapter.upsertOne(state, entity);
  state.singleView.cachedIds = chain([entity.id]).union(state.singleView.cachedIds).take(SINGLE_CACHE_SIZE).value();
  entityAdapter.pruneCache(state);
};

const subscriptionsSlice = createSlice({
  name: 'subscriptions/global',
  initialState,
  reducers: {
    resetError: state => {
      state.listView.error = null;
    },
    setPage: paginationAdapter.setPage,
    setPageSize: paginationAdapter.setPageSize,
    setFilter: filterAdapter.setFilter,
    clearAllFilters: filterAdapter.clearAllFilters,
    applyFilters: filterAdapter.applyFilters,
    resetEntityError: (state, { payload: { subscriptionId } }) => {
      entityAdapter.upsertOne(state, { id: subscriptionId, error: null });
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchSubscription.pending, (state, { meta: { arg } }) => {
      entityAdapter.upsertOnePruneCache(state, { id: arg.subscriptionId, isFetching: true, error: null });
    });
    builder.addCase(fetchSubscription.fulfilled, (state, { meta: { arg }, payload: subscription }) => {
      entityAdapter.upsertOne(state, { id: arg.subscriptionId, subscription, isFetching: false, error: null });
    });
    builder.addCase(fetchSubscription.rejected, (state, { meta: { arg }, error }) => {
      entityAdapter.upsertOne(state, { id: arg.subscriptionId, subscription: undefined, isFetching: false, error });
    });

    builder.addCase(fetchSubscriptions.pending, (state, _) => {
      patch(state.listView, {
        isFetching: true,
        error: null,
      });
    });
    builder.addCase(fetchSubscriptions.fulfilled, (state, { payload: { contents, totalCount } }) => {
      const subscriptions = normalize(contents);
      patch(state.listView, {
        ids: map(subscriptions, 'id'),
        totalCount,
        isFetching: false,
      });
      entityAdapter.upsertMany(state, subscriptions);
      entityAdapter.pruneCache(state);
    });
    builder.addCase(fetchSubscriptions.rejected, (state, { error }) => {
      patch(state.listView, {
        ids: [],
        totalCount: null,
        isFetching: false,
        error,
      });
      entityAdapter.pruneCache(state);
    });

    builder.addCase(cancelSubscription.pending, (state, { meta: { arg } }) => {
      entityAdapter.upsertOnePruneCache(state, { id: arg.subscriptionId, isFetching: true, error: null });
    });
    builder.addCase(cancelSubscription.fulfilled, (state, { meta: { arg }, payload: { subscription } }) => {
      entityAdapter.upsertOne(state, { id: arg.subscriptionId, subscription, isFetching: false, error: null });
    });
    builder.addCase(cancelSubscription.rejected, (state, { meta: { arg }, error }) => {
      entityAdapter.upsertOne(state, { id: arg.subscriptionId, isFetching: false, error });
    });
  },
});

const normalize = subscriptions =>
  map(subscriptions, subscription => ({
    id: subscription.id,
    subscription,
    isFetching: false,
    error: null,
  }));

export const {
  actions: { resetError, setPage, setPageSize, setFilter, clearAllFilters, applyFilters, resetEntityError },
  reducer: subscriptionsReducer,
} = subscriptionsSlice;

export const subscriptionsSelectors = entityAdapter.getSelectors(state => state.subscriptions.global);
